import { useState, useEffect, useCallback, useRef } from "react";
import { apiClient } from "../config/api";
import API_CONFIG from "../config/api";
import * as signalR from "@microsoft/signalr";

/**
 * SignalR Hub Service for Customer Request Chat
 */
class CustomerRequestChatHubService {
  constructor() {
    this.connection = null;
    this.connectionStateCallbacks = [];
    this.messageCallbacks = [];
    this.errorCallbacks = [];
    this.currentRequestId = null;
    this.isConnecting = false; // Flag to prevent concurrent connection attempts
  }

  async startConnection(requestId) {
    // If already connected to this request, do nothing
    if (
      this.connection &&
      this.currentRequestId === requestId &&
      this.connection.state === signalR.HubConnectionState.Connected
    ) {
      console.log("Already connected to request:", requestId);
      return;
    }

    // If currently connecting, skip to prevent duplicate connections
    if (this.isConnecting) {
      console.log("Connection already in progress, skipping...");
      return;
    }

    // If connection is in connecting state, skip
    if (this.connection?.state === signalR.HubConnectionState.Connecting) {
      console.log("Connection is already connecting, skipping...");
      return;
    }

    this.isConnecting = true;

    // Stop existing connection if any
    await this.stopConnection();

    this.currentRequestId = requestId;

    try {
      // Get auth token
      const clerk = window.Clerk;
      let token = null;
      if (clerk?.session) {
        token = await clerk.session.getToken();
      }

      // Create new connection
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(
          `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CUSTOMER_REQUEST_CHAT_HUB}`,
          {
            accessTokenFactory: () => token,
          }
        )
        .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
        .configureLogging(signalR.LogLevel.Warning)
        .build();

      // Set up event handlers
      this.connection.on("ReceiveMessage", (message) => {
        this.messageCallbacks.forEach((cb) => cb(message));
      });

      this.connection.on("Error", (errorMessage) => {
        this.errorCallbacks.forEach((cb) => cb({ message: errorMessage }));
      });

      this.connection.onreconnecting(() => {
        this.notifyConnectionState("reconnecting");
      });

      this.connection.onreconnected(() => {
        this.notifyConnectionState("connected");
        // Rejoin the room after reconnection
        this.joinRoom(requestId);
      });

      this.connection.onclose(() => {
        this.notifyConnectionState("disconnected");
      });

      // Start connection
      this.notifyConnectionState("connecting");
      await this.connection.start();
      this.notifyConnectionState("connected");

      // Join the request room
      await this.joinRoom(requestId);
    } catch (error) {
      console.error("Failed to start customer request chat connection:", error);
      this.notifyConnectionState("disconnected");
      throw error;
    } finally {
      this.isConnecting = false;
    }
  }

  async joinRoom(requestId) {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      await this.connection.invoke("JoinRequestRoom", requestId.toString());
    }
  }

  async leaveRoom(requestId) {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      await this.connection.invoke("LeaveRequestRoom", requestId.toString());
    }
  }

  async stopConnection() {
    this.isConnecting = false; // Reset connecting flag

    if (this.connection) {
      try {
        if (this.currentRequestId) {
          await this.leaveRoom(this.currentRequestId);
        }
      } catch (error) {
        console.error("Error leaving room:", error);
      }

      try {
        await this.connection.stop();
      } catch (error) {
        console.error("Error stopping connection:", error);
      }

      this.connection = null;
      this.currentRequestId = null;
      this.notifyConnectionState("disconnected");
    }
  }

  async sendMessage(content, requestId) {
    if (this.connection?.state !== signalR.HubConnectionState.Connected) {
      throw new Error("Not connected to chat hub");
    }

    await this.connection.invoke("SendMessage", {
      customerRequestId: parseInt(requestId, 10),
      content: content,
    });
  }

  isConnected() {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }

  notifyConnectionState(state) {
    this.connectionStateCallbacks.forEach((cb) => cb(state));
  }

  onConnectionStateChange(callback) {
    this.connectionStateCallbacks.push(callback);
    return () => {
      this.connectionStateCallbacks = this.connectionStateCallbacks.filter(
        (cb) => cb !== callback
      );
    };
  }

  onMessage(callback) {
    this.messageCallbacks.push(callback);
    return () => {
      this.messageCallbacks = this.messageCallbacks.filter(
        (cb) => cb !== callback
      );
    };
  }

  onError(callback) {
    this.errorCallbacks.push(callback);
    return () => {
      this.errorCallbacks = this.errorCallbacks.filter((cb) => cb !== callback);
    };
  }
}

// Singleton instance
const customerRequestChatHub = new CustomerRequestChatHubService();

/**
 * Custom hook for managing customer request chat with SignalR
 * @param {object} request - The customer request object
 * @returns {object} Chat state and methods
 */
export default function useCustomerRequestChat(request) {
  const [messages, setMessages] = useState([]);
  const [connectionState, setConnectionState] = useState("disconnected");
  const [error, setError] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const requestIdRef = useRef(request?.id);
  const historyLoadedRef = useRef(false);

  // Extract numeric ID from request
  const getNumericId = useCallback((req) => {
    if (!req?.id) return null;
    // If ID is like "REQ-123-456", extract the last number
    const idStr = req.id.toString();
    const match = idStr.match(/(\d+)$/);
    return match ? match[1] : idStr;
  }, []);

  const requestId = getNumericId(request);

  // Load message history from database
  useEffect(() => {
    if (!requestId) return;

    // Prevent loading history multiple times for the same request
    if (historyLoadedRef.current && requestIdRef.current === requestId) {
      return;
    }

    requestIdRef.current = requestId;
    historyLoadedRef.current = false;

    const loadMessageHistory = async () => {
      setIsLoadingHistory(true);
      try {
        const response = await apiClient.get(
          API_CONFIG.ENDPOINTS.CUSTOMER_REQUEST_CHAT_MESSAGES(requestId)
        );

        if (response.data && response.data.length > 0) {
          const historyMessages = response.data.map((msg) => ({
            id: msg.id,
            role: msg.role,
            content: msg.content,
            timestamp: msg.timestamp || msg.createdAt,
            userId: msg.userId,
            userName: msg.userName,
            userImageUrl: msg.userImageUrl,
          }));
          setMessages(historyMessages);
          historyLoadedRef.current = true;
        } else {
          setMessages([]);
          historyLoadedRef.current = true;
        }
      } catch (err) {
        console.error("Failed to load chat history:", err);
        setMessages([]);
        historyLoadedRef.current = true;
      } finally {
        setIsLoadingHistory(false);
      }
    };

    loadMessageHistory();
  }, [requestId]);

  // Connect to SignalR when request changes
  useEffect(() => {
    if (!requestId) return;

    const connect = async () => {
      try {
        setError(null);
        await customerRequestChatHub.startConnection(requestId);
      } catch (err) {
        console.error("Failed to connect to chat:", err);
        setError("Failed to connect to chat service.");
      }
    };

    connect();

    return () => {
      customerRequestChatHub.stopConnection();
    };
  }, [requestId]);

  // Subscribe to connection state changes
  useEffect(() => {
    const unsubscribe = customerRequestChatHub.onConnectionStateChange(
      (state) => {
        setConnectionState(state);
      }
    );

    return unsubscribe;
  }, []);

  // Subscribe to incoming messages
  useEffect(() => {
    const unsubscribe = customerRequestChatHub.onMessage((message) => {
      const chatMessage = {
        id: message.id || `msg-${Date.now()}`,
        role: message.role || "user",
        content: message.content,
        timestamp: message.timestamp || new Date().toISOString(),
        userId: message.userId,
        userName: message.userName,
        userImageUrl: message.userImageUrl,
      };

      setMessages((prev) => {
        const exists = prev.some((m) => m.id === chatMessage.id);
        if (exists) {
          return prev;
        }
        return [...prev, chatMessage];
      });
    });

    return unsubscribe;
  }, []);

  // Subscribe to errors
  useEffect(() => {
    const unsubscribe = customerRequestChatHub.onError((err) => {
      console.error("Chat error:", err);
      setError(err.message || "An error occurred");
    });

    return unsubscribe;
  }, []);

  /**
   * Send a message
   */
  const sendMessage = useCallback(
    async (content) => {
      if (!content.trim()) return;

      const messageContent = content.trim();

      if (!customerRequestChatHub.isConnected()) {
        setError("Not connected to chat. Please wait or retry connection.");
        return;
      }

      setIsSending(true);

      try {
        await customerRequestChatHub.sendMessage(messageContent, requestId);

        // Add locally for instant feedback
        const userMessage = {
          id: `local-${Date.now()}`,
          role: "user",
          content: messageContent,
          timestamp: new Date().toISOString(),
          isLocal: true,
        };
        setMessages((prev) => [...prev, userMessage]);

        setError(null);
      } catch (err) {
        console.error("Failed to send message:", err);
        setError("Failed to send message. Please try again.");
      } finally {
        setIsSending(false);
      }
    },
    [requestId]
  );

  /**
   * Clear chat history (local only)
   */
  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  /**
   * Reload message history from database
   */
  const reloadHistory = useCallback(async () => {
    if (!requestId) return;

    setIsLoadingHistory(true);
    try {
      const response = await apiClient.get(
        API_CONFIG.ENDPOINTS.CUSTOMER_REQUEST_CHAT_MESSAGES(requestId)
      );

      if (response.data && response.data.length > 0) {
        const historyMessages = response.data.map((msg) => ({
          id: msg.id,
          role: msg.role,
          content: msg.content,
          timestamp: msg.timestamp || msg.createdAt,
          userId: msg.userId,
          userName: msg.userName,
          userImageUrl: msg.userImageUrl,
        }));
        setMessages(historyMessages);
      } else {
        setMessages([]);
      }
    } catch (err) {
      console.error("Failed to reload chat history:", err);
      setError("Failed to reload chat history.");
    } finally {
      setIsLoadingHistory(false);
    }
  }, [requestId]);

  /**
   * Retry connection
   */
  const retryConnection = useCallback(async () => {
    if (requestId) {
      setError(null);
      try {
        await customerRequestChatHub.startConnection(requestId);
      } catch (err) {
        setError("Failed to reconnect. Please try again.");
      }
    }
  }, [requestId]);

  return {
    messages,
    connectionState,
    error,
    isSending,
    isLoadingHistory,
    sendMessage,
    clearMessages,
    reloadHistory,
    retryConnection,
    isConnected: connectionState === "connected",
  };
}
